import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { WsEnvelope, WsEventName } from "@wato/event-bus";
import { RealtimeClient, type ConnectionState, type RealtimeClientOptions } from "./client";

type EnvelopeListener = (envelope: WsEnvelope<WsEventName>) => void;

interface RealtimeContextValue {
  readonly state: ConnectionState;
  /** Registers `listener` for every envelope the gateway forwards; returns the unsubscribe
   * function. Sprint 3+ UI (the inbox) is the first real consumer — nothing renders from this
   * yet (WP-124 build step 12: "connection-state indicator available ..., not yet rendered as
   * UI chrome this sprint"). */
  addEnvelopeListener(listener: EnvelopeListener): () => void;
  subscribe(workspaceId: string, conversationId?: string, topic?: "whisper"): void;
  unsubscribe(workspaceId: string, conversationId?: string, topic?: "whisper"): void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export interface RealtimeProviderProps {
  readonly children: React.ReactNode;
  /** Same-origin WS URL, e.g. `() => \`\${location.protocol === "https:" ? "wss" : "ws"}://\${location.host}/v1/realtime\`` — a plain path also works since the browser resolves it against the page origin. Vite's dev proxy (`vite.config.ts`) forwards `/v1/realtime` to the API with `ws: true`, same as `/v1/*` REST calls. */
  readonly url?: () => string;
  /** Test/story seam — see `RealtimeClientOptions`. */
  readonly clientOptions?: Partial<
    Omit<RealtimeClientOptions, "url" | "onEnvelope" | "onStateChange">
  >;
}

function defaultUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/v1/realtime`;
}

/**
 * Owns one `RealtimeClient` for the app's lifetime (WP-124 build step 10: "connect on load").
 * Auto-subscribing to the *active* workspace is `WorkspaceShell`'s job (it alone knows which
 * workspace is active) via `useRealtimeSubscription(workspaceId)` below — this provider only
 * starts the socket and lets any descendant subscribe/unsubscribe/listen.
 */
export function RealtimeProvider({
  children,
  url = defaultUrl,
  clientOptions,
}: RealtimeProviderProps) {
  const [state, setState] = useState<ConnectionState>("closed");
  const listenersRef = useRef(new Set<EnvelopeListener>());
  const clientRef = useRef<RealtimeClient | null>(null);

  if (!clientRef.current) {
    clientRef.current = new RealtimeClient({
      url,
      onStateChange: setState,
      onEnvelope: (envelope) => {
        for (const listener of listenersRef.current) listener(envelope);
      },
      ...clientOptions,
    });
  }

  useEffect(() => {
    const client = clientRef.current;
    client?.start();
    return () => client?.stop();
    // One `RealtimeClient` for the provider's whole lifetime: intentionally not re-run on
    // `url`/`clientOptions` changes (a workspace switch subscribes to a new channel, it never
    // needs a new socket).
  }, []);

  // Stable across renders (the client itself never changes for the provider's lifetime), so a
  // consumer's `useEffect([subscribe, unsubscribe, ...])` below never re-fires just because the
  // connection state changed.
  const actions = useRef({
    addEnvelopeListener: (listener: EnvelopeListener) => {
      listenersRef.current.add(listener);
      return () => listenersRef.current.delete(listener);
    },
    subscribe: (workspaceId: string, conversationId?: string, topic?: "whisper") =>
      clientRef.current?.subscribe(workspaceId, conversationId, topic),
    unsubscribe: (workspaceId: string, conversationId?: string, topic?: "whisper") =>
      clientRef.current?.unsubscribe(workspaceId, conversationId, topic),
  }).current;

  const value = useMemo<RealtimeContextValue>(() => ({ state, ...actions }), [state, actions]);

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

function useRealtime(): RealtimeContextValue {
  const value = useContext(RealtimeContext);
  if (!value) throw new Error("useRealtime* hooks must be used within <RealtimeProvider>");
  return value;
}

/** The gateway's current connection state — not yet rendered anywhere (WP-124); a future
 * connection-status chip reads this. */
export function useRealtimeConnectionState(): ConnectionState {
  return useRealtime().state;
}

/** Auto-subscribes to `workspaceId` (and `conversationId`, if given) for as long as the
 * calling component is mounted, and on every reconnect (`RealtimeClient` replays it). */
export function useRealtimeSubscription(
  workspaceId: string,
  conversationId?: string,
  topic?: "whisper",
): void {
  const { subscribe, unsubscribe } = useRealtime();
  useEffect(() => {
    subscribe(workspaceId, conversationId, topic);
    return () => unsubscribe(workspaceId, conversationId, topic);
  }, [workspaceId, conversationId, topic, subscribe, unsubscribe]);
}

/** Registers `listener` for every envelope while the calling component is mounted. */
export function useRealtimeEvent(listener: EnvelopeListener): void {
  const { addEnvelopeListener } = useRealtime();
  const listenerRef = useRef(listener);
  listenerRef.current = listener;
  useEffect(
    () => addEnvelopeListener((envelope) => listenerRef.current(envelope)),
    [addEnvelopeListener],
  );
}
